import {
  browserAccountStateSessionRepository,
} from "../account/account-state-session-repository.js";
import { readTrackerBetaAccessV2 } from "../account/tracker-beta-access.js";
import "../components/catch-log-view.js";
import "../components/tracker-participation.js";

const ACCESS_PATH = "/api/v1/account/tracker-beta";

function apiUrl(path) {
  const resolve = globalThis.window?.__fishystuffResolveApiUrl;
  if (typeof resolve !== "function") {
    throw new TypeError("Fishystuff API URL resolver is unavailable.");
  }
  return resolve(path);
}

class FishyCatchLogBeta extends HTMLElement {
  #repository = null;
  #unsubscribe = null;
  #request = null;
  #lifecycle = null;
  #accountId = null;
  #authorizedNodes = [];
  #releaseKind = "unavailable";
  #enabled = false;
  #page = "activity";

  connectedCallback() {
    if (this.#unsubscribe) return;
    if (window.location.hash === "#participation") this.#page = "participation";
    this.#lifecycle = new AbortController();
    this.addEventListener("click", (event) => {
      const notice = event.target.closest?.("[data-catch-log-open-participation]");
      if (notice && this.contains(notice)) {
        event.preventDefault();
        this.#page = "participation";
        this.#renderPageSelection(this.#enabled);
        this.querySelector('[data-catch-log-page-tab="participation"]')?.focus();
        return;
      }
      const tab = event.target.closest?.("[data-catch-log-page-tab]");
      if (!tab || !this.contains(tab) || tab.disabled) return;
      this.#page = tab.dataset.catchLogPageTab;
      this.#renderPageSelection(this.#enabled);
    }, { signal: this.#lifecycle.signal });
    this.addEventListener("keydown", (event) => {
      const tab = event.target.closest?.("[data-catch-log-page-tab]");
      if (!tab || !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
      const tabs = [...this.querySelectorAll("[data-catch-log-page-tab]")].filter(item => !item.disabled);
      const index = tabs.indexOf(tab);
      if (index < 0) return;
      event.preventDefault();
      const next = event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1
        : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
      tabs[next].click();
      tabs[next].focus();
    }, { signal: this.#lifecycle.signal });
    this.#repository = browserAccountStateSessionRepository();
    this.#unsubscribe = this.#repository.subscribe((state) => this.#sessionChanged(state));
    void this.#repository.load();
  }

  disconnectedCallback() {
    this.#lifecycle?.abort();
    this.#lifecycle = null;
    this.#request?.abort();
    this.#request = null;
    this.#unmountAuthorizedLog();
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  #sessionChanged(state) {
    if (["uninitialized", "loading"].includes(state.kind)) {
      this.#cancelAccessRequest();
      this.#show("loading");
      return;
    }
    if (state.kind === "signedOut") {
      this.#cancelAccessRequest();
      this.#accountId = null;
      this.#show("signed-out");
      return;
    }
    if (state.kind === "unavailable") {
      this.#cancelAccessRequest();
      this.#accountId = null;
      this.#show("unavailable");
      return;
    }
    if (state.kind === "ready" && state.accountId !== this.#accountId) {
      this.#accountId = state.accountId;
      void this.#loadAccess(state);
    }
  }

  async #loadAccess(session) {
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    this.#show("loading");
    try {
      const result = await readTrackerBetaAccessV2({
        url: apiUrl(ACCESS_PATH),
        signal: request.signal,
      });
      if (request.signal.aborted || session.accountId !== this.#accountId) return;
      if (result.kind === "signedOut") {
        this.#show("signed-out");
        return;
      }
      if (result.kind === "disabled") {
        this.#show("disabled");
        return;
      }
      if (request.signal.aborted || session.accountId !== this.#accountId) return;
      this.#renderAccess(result.access);
      this.#show("enabled");
      this.#mountAuthorizedLog();
    } catch (error) {
      if (!request.signal.aborted && error?.name !== "AbortError") {
        this.#show("unavailable");
      }
    } finally {
      if (this.#request === request) this.#request = null;
    }
  }

  #renderAccess(access) {
    this.#releaseKind = access.release.kind;
    if (access.release.kind === "unavailable") return;
    const version = this.querySelector("[data-tracker-release-version]");
    if (version) version.textContent = access.release.version;
    const installs = this.querySelector("[data-desktop-installs]");
    if (!installs) return;
    installs.replaceChildren();
    const installChoices = [{
      title: "Nix profile",
      detail: `nix profile install ${access.release.nix.flakeReference}#${access.release.nix.packageAttribute}`,
    }];
    if (access.release.kind === "available") {
      installChoices.unshift({
        title: "AppImage · Linux x86_64",
        detail: `SHA-256 ${access.release.appImage.sha256}`,
        href: access.release.appImage.url,
        action: "Download AppImage",
      });
    }
    for (const install of installChoices) {
      const card = document.createElement("div");
      card.className = "rounded-box border border-base-300 bg-base-200/40 p-4";
      const row = document.createElement("div");
      row.className = "flex flex-wrap items-center justify-between gap-3";
      const copy = document.createElement("div");
      const title = document.createElement("p");
      title.className = "font-semibold";
      title.textContent = install.title;
      const checksum = document.createElement("code");
      checksum.className = "mt-1 block break-all text-xs text-base-content/60";
      checksum.textContent = install.detail;
      copy.append(title, checksum);
      row.append(copy);
      if (install.href) {
        const link = document.createElement("a");
        link.className = "btn btn-secondary btn-sm";
        link.href = install.href;
        link.textContent = install.action;
        link.rel = "noopener";
        row.append(link);
      }
      card.append(row);
      installs.append(card);
    }
  }

  #mountAuthorizedLog() {
    if (this.#authorizedNodes.length > 0) return;
    const template = this.querySelector("template[data-catch-log-authorized-template]");
    if (!(template instanceof HTMLTemplateElement)) {
      this.#show("unavailable");
      return;
    }
    const fragment = template.content.cloneNode(true);
    this.#authorizedNodes = [...fragment.childNodes];
    const activity = this.querySelector('[data-catch-log-page-panel="activity"]');
    if (!activity) {
      this.#show("unavailable");
      return;
    }
    activity.append(fragment);
  }

  #unmountAuthorizedLog() {
    for (const node of this.#authorizedNodes) node.remove();
    this.#authorizedNodes = [];
  }

  #cancelAccessRequest() {
    this.#request?.abort();
    this.#request = null;
  }

  #show(state) {
    if (state !== "enabled") {
      this.#unmountAuthorizedLog();
    }
    const visibility = {
      "[data-catch-log-gate-loading]": state === "loading",
      "[data-catch-log-gate-signed-out]": state === "signed-out",
      "[data-catch-log-gate-disabled]": state === "disabled",
      "[data-catch-log-gate-unavailable]": state === "unavailable",
      "[data-catch-log-beta-setup]": state === "enabled"
        && (this.#releaseKind === "available" || this.#releaseKind === "nix"),
      "[data-catch-log-beta-release-unavailable]": state === "enabled" && this.#releaseKind === "unavailable",
    };
    for (const [selector, visible] of Object.entries(visibility)) {
      const element = this.querySelector(selector);
      if (element) element.hidden = !visible;
    }
    this.#enabled = state === "enabled";
    const tabs = this.querySelector("[data-catch-log-page-tabs]");
    if (tabs) tabs.hidden = false;
    this.#renderPageSelection(this.#enabled);
  }

  #renderPageSelection(enabled) {
    if (!enabled && this.#page === "download") this.#page = "activity";
    const page = ["download", "participation"].includes(this.#page) ? this.#page : "activity";
    for (const tab of this.querySelectorAll("[data-catch-log-page-tab]")) {
      tab.disabled = !enabled && tab.dataset.catchLogPageTab === "download";
      const selected = tab.dataset.catchLogPageTab === page;
      tab.classList.toggle("tab-active", selected);
      tab.setAttribute("aria-selected", String(selected));
      tab.tabIndex = selected ? 0 : -1;
    }
    for (const panel of this.querySelectorAll("[data-catch-log-page-panel]")) {
      panel.hidden = panel.dataset.catchLogPagePanel !== page;
    }
  }
}

if (!customElements.get("fishy-catch-log-beta")) {
  customElements.define("fishy-catch-log-beta", FishyCatchLogBeta);
}
